import React, { ComponentProps, MouseEvent } from 'react'
import { Fragment, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationCircleIcon, ExclamationIcon } from '@heroicons/react/outline'
import isFunction from 'lodash/isFunction'
import clsx from 'clsx'

type DeleteButtonType = {
  modalTitle: string
  modalText?: string
  buttonText?: string
  alert?: boolean
} & ComponentProps<'button'>

const DeleteButton: React.FC<DeleteButtonType> = (props) => {
  const { children, modalText, modalTitle, onClick, alert, ...rest } = props
  const [open, setOpen] = useState(false)

  const cancelButtonRef = useRef(null)

  const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
    if (isFunction(onClick)) {
      onClick(e)
    }
    setOpen(false)
  }

  return (
    <Fragment>
      <button onClick={() => setOpen(true)} {...rest}>
        {children}
      </button>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div
                      className={clsx({
                        'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10':
                          true,
                        'bg-red-100': !alert,
                        'bg-yellow-100': alert,
                      })}
                    >
                      {alert ? (
                        <ExclamationCircleIcon className="h-6 w-6 text-yellow-800" aria-hidden="true" />
                      ) : (
                        <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                      )}
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="page-title">
                        {modalTitle}
                      </Dialog.Title>
                      {modalText ? (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">{modalText}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className={clsx({
                      'btn inline-flex w-full justify-center sm:ml-3 sm:w-auto': true,
                      'btn-delete': !alert,
                      'btn-primary': alert,
                    })}
                    onClick={handleDelete}
                  >
                    {props.buttonText ? props.buttonText : children}
                  </button>
                  <button
                    type="button"
                    className="btn btn-white mt-3 inline-flex w-full justify-center sm:mt-0 sm:ml-3 sm:w-auto"
                    onClick={() => setOpen(false)}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </Fragment>
  )
}

export default DeleteButton
